/**
 * The ClickHouse column types the stream forms offer, and the display formatters that can be
 * attached to a numeric column. Both are long, flat option lists the backend defines — kept out of
 * `field-utils` so the logic there stays readable.
 */

// Field type options for the form
export const FIELD_TYPES: Array<{ label: string; value: string }> = [
	{ value: 'Bool', label: 'Bool' },
	{ value: 'Nullable(Bool)', label: 'Nullable(Bool)' },
	{ value: 'Float32', label: 'Float32' },
	{ value: 'Nullable(Float32)', label: 'Nullable(Float32)' },
	{ value: 'Float64', label: 'Float64' },
	{ value: 'Nullable(Float64)', label: 'Nullable(Float64)' },
	{ value: 'Int8', label: 'Int8' },
	{ value: 'Nullable(Int8)', label: 'Nullable(Int8)' },
	{ value: 'Int16', label: 'Int16' },
	{ value: 'Nullable(Int16)', label: 'Nullable(Int16)' },
	{ value: 'Int32', label: 'Int32' },
	{ value: 'Nullable(Int32)', label: 'Nullable(Int32)' },
	{ value: 'Int64', label: 'Int64' },
	{ value: 'Nullable(Int64)', label: 'Nullable(Int64)' },
	{ value: 'UInt8', label: 'UInt8' },
	{ value: 'Nullable(UInt8)', label: 'Nullable(UInt8)' },
	{ value: 'UInt16', label: 'UInt16' },
	{ value: 'Nullable(UInt16)', label: 'Nullable(UInt16)' },
	{ value: 'UInt32', label: 'UInt32' },
	{ value: 'Nullable(UInt32)', label: 'Nullable(UInt32)' },
	{ value: 'UInt64', label: 'UInt64' },
	{ value: 'Nullable(UInt64)', label: 'Nullable(UInt64)' },
	{ value: 'String', label: 'String' },
	{ value: 'Nullable(String)', label: 'Nullable(String)' },
	{ value: 'Date', label: 'Date' },
	{ value: 'Nullable(Date)', label: 'Nullable(Date)' },
	{ value: 'DateTime', label: 'DateTime' },
	{ value: 'Nullable(DateTime)', label: 'Nullable(DateTime)' },
	{ value: 'DateTime64(3)', label: 'Timestamp (ms)' },
	{ value: 'Nullable(DateTime64(3))', label: 'Nullable Timestamp (ms)' },
	{ value: 'DateTime64(6)', label: 'Timestamp (μs)' },
	{ value: 'Nullable(DateTime64(6))', label: 'Nullable Timestamp (μs)' },
]

// Formatter options for numeric types (display/formatting)
export const FORMATTER_OPTIONS: Array<{ label: string; value: string }> = [
	{ label: 'None', value: 'none' },
	// ==================== Custom Options ====================
	// '%'
	{
		label: '%',
		value: '%',
	},
	{
		label: 'ohm',
		value: 'ohm',
	},

	// ==================== Length ====================
	// 'nm' | 'μm' | 'mm' | 'cm' | 'm' | 'km' | 'mil'| 'in'| 'yd'| 'ft-us'| 'ft'| 'fathom'| 'mi'| 'nMi'
	{
		label: 'nm',
		value: 'nm',
	},
	{
		label: 'μm',
		value: 'μm',
	},
	{
		label: 'mm',
		value: 'mm',
	},
	{
		label: 'cm',
		value: 'cm',
	},
	{
		label: 'm',
		value: 'm',
	},
	{
		label: 'km',
		value: 'km',
	},
	{
		label: 'mil',
		value: 'mil',
	},
	{
		label: 'in',
		value: 'in',
	},
	{
		label: 'yd',
		value: 'yd',
	},
	{
		label: 'ft-us',
		value: 'ft-us',
	},
	{
		label: 'ft',
		value: 'ft',
	},
	{
		label: 'fathom',
		value: 'fathom',
	},
	{
		label: 'mi',
		value: 'mi',
	},
	{
		label: 'nMi',
		value: 'nMi',
	},

	// ==================== Acceleration ====================
	// 'm/s^2' | 'g-force'
	{
		label: 'm/s^2',
		value: 'm/s2',
	},
	{
		label: 'g-force',
		value: 'g-force',
	},

	// ==================== Force ====================
	// 'N' | 'kN' | 'lbf' | 'kgf'
	{
		label: 'N',
		value: 'N',
	},
	{
		label: 'kN',
		value: 'kN',
	},
	{
		label: 'lbf',
		value: 'lbf',
	},
	{
		label: 'kgf',
		value: 'kgf',
	},

	// ==================== Area ====================
	// 'nm^2' | 'µm^2' | 'mm^2' | 'cm^2' | 'm^2' | 'ha' | 'km^2' | 'in^2' | 'yd^2' | 'ft^2' | 'ac' | 'mi^2'
	{
		label: 'nm^2',
		value: 'nm2',
	},
	{
		label: 'µm^2',
		value: 'µm2',
	},
	{
		label: 'mm^2',
		value: 'mm2',
	},
	{
		label: 'cm^2',
		value: 'cm2',
	},
	{
		label: 'm^2',
		value: 'm2',
	},
	{
		label: 'ha',
		value: 'ha',
	},
	{
		label: 'km^2',
		value: 'km2',
	},
	{
		label: 'in^2',
		value: 'in2',
	},
	{
		label: 'yd^2',
		value: 'yd2',
	},
	{
		label: 'ft^2',
		value: 'ft2',
	},
	{
		label: 'ac',
		value: 'ac',
	},
	{
		label: 'mi^2',
		value: 'mi2',
	},

	// ==================== Charge ====================
	// 'c' | 'mC' | 'μC' | 'nC' | 'pC'
	// {
	//   label: "c",
	//   value: "c",
	// },
	{
		label: 'mC',
		value: 'mC',
	},
	{
		label: 'μC',
		value: 'μC',
	},
	{
		label: 'nC',
		value: 'nC',
	},
	{
		label: 'pC',
		value: 'pC',
	},

	// ==================== Mass ====================
	// 'mcg' | 'mg' | 'g' | 'kg' | 'mt' | 'oz' | 'lb' | 'st' | 't'
	{
		label: 'mcg',
		value: 'mcg',
	},
	{
		label: 'mg',
		value: 'mg',
	},
	{
		label: 'g',
		value: 'g',
	},
	{
		label: 'kg',
		value: 'kg',
	},
	{
		label: 'mt',
		value: 'mt',
	},
	{
		label: 'oz',
		value: 'oz',
	},
	{
		label: 'lb',
		value: 'lb',
	},
	{
		label: 'st',
		value: 'st',
	},
	{
		label: 't',
		value: 't',
	},

	// ==================== Mass Flow Rate ====================
	// 'kg/s' | 'kg/h' | 'mt/h' | 'lb/s' | 'lb/h'
	{
		label: 'kg/s',
		value: 'kg/s',
	},
	{
		label: 'kg/h',
		value: 'kg/h',
	},
	{
		label: 'mt/h',
		value: 'mt/h',
	},
	{
		label: 'lb/s',
		value: 'lb/s',
	},
	{
		label: 'lb/h',
		value: 'lb/h',
	},

	// ==================== Volume ====================
	// 'mm3' | 'cm3' | 'ml' | 'l' | 'kl' | 'Ml' | 'Gl' | 'm3' | 'km3' | 'cl' | 'dl' | 'krm' | 'tsk' | 'msk' | 'kkp' | 'glas' | 'kanna'
	// 'tsp' | 'Tbs' | 'in3' | 'fl-oz' | 'cup' | 'pnt' | 'qt' | 'gal' | 'ft3' | 'yd3'
	{
		label: 'mm^3',
		value: 'mm3',
	},
	{
		label: 'cm^3',
		value: 'cm3',
	},
	{
		label: 'ml',
		value: 'ml',
	},
	{
		label: 'l',
		value: 'l',
	},
	{
		label: 'kl',
		value: 'kl',
	},
	{
		label: 'Ml',
		value: 'Ml',
	},
	{
		label: 'Gl',
		value: 'Gl',
	},
	{
		label: 'm^3',
		value: 'm3',
	},
	{
		label: 'km^3',
		value: 'km3',
	},
	{
		label: 'cl',
		value: 'cl',
	},
	{
		label: 'dl',
		value: 'dl',
	},
	{
		label: 'krm',
		value: 'krm',
	},
	{
		label: 'tsk',
		value: 'tsk',
	},
	{
		label: 'msk',
		value: 'msk',
	},
	{
		label: 'kkp',
		value: 'kkp',
	},
	{
		label: 'glas',
		value: 'glas',
	},
	{
		label: 'kanna',
		value: 'kanna',
	},
	{
		label: 'tsp',
		value: 'tsp',
	},
	{
		label: 'Tbs',
		value: 'Tbs',
	},
	{
		label: 'in^3',
		value: 'in3',
	},
	{
		label: 'fl-oz',
		value: 'fl-oz',
	},
	{
		label: 'cup',
		value: 'cup',
	},
	{
		label: 'pnt',
		value: 'pnt',
	},
	{
		label: 'qt',
		value: 'qt',
	},
	{
		label: 'gal',
		value: 'gal',
	},
	{
		label: 'ft^3',
		value: 'ft3',
	},
	{
		label: 'yd^3',
		value: 'yd3',
	},

	// ==================== Volume Flow Rate ====================
	// 'mm3/s' | 'cm3/s' | 'ml/s' | 'cl/s' | 'dl/s' | 'l/s' | 'l/min' | 'l/h' | 'kl/s' | 'kl/min' | 'kl/h' | 'm3/s' | 'm3/min' | 'm3/h' | 'km3/s'
	// 'tsp/s' | 'Tbs/s' | 'in3/s' | 'in3/min' | 'in3/h' | 'fl-oz/s' | 'fl-oz/min' | 'fl-oz/h' | 'cup/s' | 'pnt/s' | 'pnt/min' | 'pnt/h' | 'qt/s' | 'gal/s' | 'gal/min' | 'gal/h' | 'ft3/s' | 'ft3/min' | 'ft3/h' | 'yd3/s' | 'yd3/min' | 'yd3/h'
	{
		label: 'mm^3/s',
		value: 'mm3/s',
	},
	{
		label: 'cm^3/s',
		value: 'cm3/s',
	},
	{
		label: 'ml/s',
		value: 'ml/s',
	},
	{
		label: 'cl/s',
		value: 'cl/s',
	},
	{
		label: 'dl/s',
		value: 'dl/s',
	},
	{
		label: 'l/s',
		value: 'l/s',
	},
	{
		label: 'l/min',
		value: 'l/min',
	},
	{
		label: 'l/h',
		value: 'l/h',
	},
	{
		label: 'kl/s',
		value: 'kl/s',
	},
	{
		label: 'kl/min',
		value: 'kl/min',
	},
	{
		label: 'kl/h',
		value: 'kl/h',
	},
	{
		label: 'm^3/s',
		value: 'm3/s',
	},
	{
		label: 'm^3/min',
		value: 'm3/min',
	},
	{
		label: 'm^3/h',
		value: 'm3/h',
	},
	{
		label: 'km^3/s',
		value: 'km3/s',
	},
	{
		label: 'tsp/s',
		value: 'tsp/s',
	},
	{
		label: 'Tbs/s',
		value: 'Tbs/s',
	},
	{
		label: 'in^3/s',
		value: 'in3/s',
	},
	{
		label: 'in^3/min',
		value: 'in3/min',
	},
	{
		label: 'in^3/h',
		value: 'in3/h',
	},
	{
		label: 'fl-oz/s',
		value: 'fl-oz/s',
	},
	{
		label: 'fl-oz/min',
		value: 'fl-oz/min',
	},
	{
		label: 'fl-oz/h',
		value: 'fl-oz/h',
	},
	{
		label: 'cup/s',
		value: 'cup/s',
	},
	{
		label: 'pnt/s',
		value: 'pnt/s',
	},
	{
		label: 'pnt/min',
		value: 'pnt/min',
	},
	{
		label: 'pnt/h',
		value: 'pnt/h',
	},
	{
		label: 'qt/s',
		value: 'qt/s',
	},
	{
		label: 'gal/s',
		value: 'gal/s',
	},
	{
		label: 'gal/min',
		value: 'gal/min',
	},
	{
		label: 'gal/h',
		value: 'gal/h',
	},
	{
		label: 'ft^3/s',
		value: 'ft3/s',
	},
	{
		label: 'ft^3/min',
		value: 'ft3/min',
	},
	{
		label: 'ft^3/h',
		value: 'ft3/h',
	},
	{
		label: 'yd^3/s',
		value: 'yd3/s',
	},
	{
		label: 'yd^3/min',
		value: 'yd3/min',
	},
	{
		label: 'yd^3/h',
		value: 'yd3/h',
	},

	// ==================== Temperature ====================
	// 'C' | 'K' | 'F' | 'R'
	{
		label: 'C',
		value: 'C',
	},
	{
		label: 'K',
		value: 'K',
	},
	{
		label: 'F',
		value: 'F',
	},
	{
		label: 'R',
		value: 'R',
	},

	// ==================== Time ====================
	// 'ns' | 'mu' | 'ms' | 's' | 'min' | 'h' | 'd' | 'week' | 'month' | 'year'
	{
		label: 'ns',
		value: 'ns',
	},
	{
		label: 'mu',
		value: 'mu',
	},
	{
		label: 'ms',
		value: 'ms',
	},
	{
		label: 's',
		value: 's',
	},
	{
		label: 'min',
		value: 'min',
	},
	{
		label: 'h',
		value: 'h',
	},
	{
		label: 'd',
		value: 'd',
	},
	{
		label: 'week',
		value: 'week',
	},
	{
		label: 'month',
		value: 'month',
	},
	{
		label: 'year',
		value: 'year',
	},

	// ==================== Frequency ====================
	//  'mHz'| 'Hz'| 'kHz'| 'MHz'| 'GHz'| 'THz'| 'rpm'| 'deg/s'| 'rad/s'
	{
		label: 'mHz',
		value: 'mHz',
	},
	{
		label: 'Hz',
		value: 'Hz',
	},
	{
		label: 'kHz',
		value: 'kHz',
	},
	{
		label: 'MHz',
		value: 'MHz',
	},
	{
		label: 'GHz',
		value: 'GHz',
	},
	{
		label: 'THz',
		value: 'THz',
	},
	{
		label: 'rpm',
		value: 'rpm',
	},
	{
		label: 'deg/s',
		value: 'deg/s',
	},
	{
		label: 'rad/s',
		value: 'rad/s',
	},

	// ==================== Speed ====================
	// 'm/s' | 'km/h' | 'mm/h' | 'mph' | 'knot' | 'ft/s' | 'ft/min' | 'in/h'
	{
		label: 'm/s',
		value: 'm/s',
	},
	{
		label: 'km/h',
		value: 'km/h',
	},
	{
		label: 'mm/h',
		value: 'mm/h',
	},
	{
		label: 'mph',
		value: 'mph',
	},
	{
		label: 'knot',
		value: 'knot',
	},
	{
		label: 'ft/s',
		value: 'ft/s',
	},
	{
		label: 'ft/min',
		value: 'ft/min',
	},
	{
		label: 'in/h',
		value: 'in/h',
	},

	// ==================== Pace ====================
	// 'min/km' | 's/m' | 'min/mi' | 's/ft'
	{
		label: 'min/km',
		value: 'min/km',
	},
	{
		label: 's/m',
		value: 's/m',
	},
	{
		label: 'min/mi',
		value: 'min/mi',
	},
	{
		label: 's/ft',
		value: 's/ft',
	},

	// ==================== Pressure ====================
	// 'Pa' | 'kPa' | 'MPa' | 'hPa' | 'bar' | 'torr' | 'mH2O' | 'mmHg' | 'psi' | 'ksi' | 'inHg'
	{
		label: 'Pa',
		value: 'Pa',
	},
	{
		label: 'kPa',
		value: 'kPa',
	},
	{
		label: 'MPa',
		value: 'MPa',
	},
	{
		label: 'hPa',
		value: 'hPa',
	},
	{
		label: 'bar',
		value: 'bar',
	},
	{
		label: 'torr',
		value: 'torr',
	},
	{
		label: 'mH2O',
		value: 'mH2O',
	},
	{
		label: 'mmHg',
		value: 'mmHg',
	},
	{
		label: 'psi',
		value: 'psi',
	},
	{
		label: 'ksi',
		value: 'ksi',
	},
	{
		label: 'inHg',
		value: 'inHg',
	},

	// ==================== Torque ====================
	// Nm' | 'lbf-ft'
	{
		label: 'Nm',
		value: 'Nm',
	},
	{
		label: 'lbf-ft',
		value: 'lbf-ft',
	},

	// ==================== Digital ====================
	// 'b' | 'Kb' | 'Mb' | 'Gb' | 'Tb' | 'B' | 'KB' | 'MB' | 'GB' | 'TB'
	{
		label: 'b',
		value: 'b',
	},
	{
		label: 'Kb',
		value: 'Kb',
	},
	{
		label: 'Mb',
		value: 'Mb',
	},
	{
		label: 'Gb',
		value: 'Gb',
	},
	{
		label: 'Tb',
		value: 'Tb',
	},
	{
		label: 'B',
		value: 'B',
	},
	{
		label: 'KB',
		value: 'KB',
	},
	{
		label: 'MB',
		value: 'MB',
	},
	{
		label: 'GB',
		value: 'GB',
	},
	{
		label: 'TB',
		value: 'TB',
	},

	// ==================== Illuminance ====================
	{
		label: 'lx',
		value: 'lx',
	},
	{
		label: 'ft-cd',
		value: 'ft-cd',
	},

	// ==================== Parts-Per ====================
	// 'ppm' | 'ppb' | 'ppt' | 'ppq'
	{
		label: 'ppm',
		value: 'ppm',
	},
	{
		label: 'ppb',
		value: 'ppb',
	},
	{
		label: 'ppt',
		value: 'ppt',
	},
	{
		label: 'ppq',
		value: 'ppq',
	},

	// ==================== Voltage ====================
	// 'V' | 'mV' | 'kV'
	{
		label: 'V',
		value: 'V',
	},
	{
		label: 'mV',
		value: 'mV',
	},
	{
		label: 'kV',
		value: 'kV',
	},

	// ==================== Current ====================
	// 'A' | 'mA' | 'kA'
	{
		label: 'A',
		value: 'A',
	},
	{
		label: 'mA',
		value: 'mA',
	},
	{
		label: 'kA',
		value: 'kA',
	},

	// ==================== Power ====================
	// 'W' | 'mW' | 'kW' | 'MW' | 'GW' | 'PS' | 'Btu/s' | 'ft-lb/s' | 'hp'
	{
		label: 'W',
		value: 'W',
	},
	{
		label: 'mW',
		value: 'mW',
	},
	{
		label: 'kW',
		value: 'kW',
	},
	{
		label: 'MW',
		value: 'MW',
	},
	{
		label: 'GW',
		value: 'GW',
	},
	{
		label: 'PS',
		value: 'PS',
	},
	{
		label: 'Btu/s',
		value: 'Btu/s',
	},
	{
		label: 'ft-lb/s',
		value: 'ft-lb/s',
	},
	{
		label: 'hp',
		value: 'hp',
	},

	// ==================== Apparent Power ====================
	// 'VA' | 'mVA' | 'kVA' | 'MVA' | 'GVA'
	{
		label: 'VA',
		value: 'VA',
	},
	{
		label: 'mVA',
		value: 'mVA',
	},
	{
		label: 'kVA',
		value: 'kVA',
	},
	{
		label: 'MVA',
		value: 'MVA',
	},
	{
		label: 'GVA',
		value: 'GVA',
	},

	// ==================== Reactive Power ====================
	// 'VAR' | 'mVAR' | 'kVAR' | 'MVAR' | 'GVAR'
	{
		label: 'VAR',
		value: 'VAR',
	},
	{
		label: 'mVAR',
		value: 'mVAR',
	},
	{
		label: 'kVAR',
		value: 'kVAR',
	},
	{
		label: 'MVAR',
		value: 'MVAR',
	},
	{
		label: 'GVAR',
		value: 'GVAR',
	},

	// ==================== Energy ====================
	// | 'Ws'| 'Wm'| 'Wh'| 'mWh'| 'kWh'| 'MWh'| 'GWh'| 'J'| 'kJ'| 'MJ'| 'GJ' | 'kcal' | 'cal'
	{
		label: 'Ws',
		value: 'Ws',
	},
	{
		label: 'Wm',
		value: 'Wm',
	},
	{
		label: 'Wh',
		value: 'Wh',
	},
	{
		label: 'mWh',
		value: 'mWh',
	},
	{
		label: 'kWh',
		value: 'kWh',
	},
	{
		label: 'MWh',
		value: 'MWh',
	},
	{
		label: 'GWh',
		value: 'GWh',
	},
	{
		label: 'J',
		value: 'J',
	},
	{
		label: 'kJ',
		value: 'kJ',
	},
	{
		label: 'kcal',
		value: 'kcal',
	},
	{
		label: 'cal',
		value: 'cal',
	},
	// ==================== Reactive Energy ====================
	// 'VARh' | 'mVARh' | 'kVARh' | 'MVARh' | 'GVARh'
	{
		label: 'VARh',
		value: 'VARh',
	},
	{
		label: 'mVARh',
		value: 'mVARh',
	},
	{
		label: 'kVARh',
		value: 'kVARh',
	},
	{
		label: 'MVARh',
		value: 'MVARh',
	},
	{
		label: 'GVARh',
		value: 'GVARh',
	},

	// ==================== Angle ====================
	// 'rad' | 'deg' | 'grad' | 'arcmin' | 'arcsec'
	{
		label: 'deg',
		value: 'deg',
	},
	{
		label: 'rad',
		value: 'rad',
	},
	{
		label: 'grad',
		value: 'grad',
	},
	{
		label: 'arcmin',
		value: 'arcmin',
	},
	{
		label: 'arcsec',
		value: 'arcsec',
	},

	// ==================== Each ====================
	// 'ea' | 'dz'
	{
		label: 'ea',
		value: 'ea',
	},
	{
		label: 'dz',
		value: 'dz',
	},

	// ==================== Pieces ====================
	// 'pcs' | 'bk-doz' | 'cp' | 'doz-doz' | 'doz' | 'gr-gr' | 'gros' | 'half-dozen' | 'long-hundred' | 'ream' | 'scores' | 'sm-gr' | 'trio'
	{
		label: 'pcs',
		value: 'pcs',
	},
	{
		label: 'bk-doz',
		value: 'bk-doz',
	},
	{
		label: 'cp',
		value: 'cp',
	},
	{
		label: 'doz-doz',
		value: 'doz-doz',
	},
	{
		label: 'doz',
		value: 'doz',
	},
	{
		label: 'gr-gr',
		value: 'gr-gr',
	},
	{
		label: 'gros',
		value: 'gros',
	},
	{
		label: 'half-dozen',
		value: 'half-dozen',
	},
	{
		label: 'long-hundred',
		value: 'long-hundred',
	},
	{
		label: 'ream',
		value: 'ream',
	},
	{
		label: 'scores',
		value: 'scores',
	},
	{
		label: 'sm-gr',
		value: 'sm-gr',
	},
	{
		label: 'trio',
		value: 'trio',
	},
]

// Field type options for adding new columns — Nullable variants only.
export const NULLABLE_FIELD_TYPES: Array<{ label: string; value: string }> = FIELD_TYPES.filter(option =>
	option.value.startsWith('Nullable('),
)
